import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login, loginWithGoogle, setGuest, user, guest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !guest) navigate("/");
  }, [user, guest]); // eslint-disable-line

  const onLogin = async () => {
    if (!identifier || !password) return toast.error("أكمل البيانات");
    setBusy(true);
    try {
      await login(identifier.trim(), password);
      navigate(location.state?.from || "/");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleSuccess = async (credentialResponse) => {
    try {
      await loginWithGoogle(credentialResponse.credential);
      navigate(location.state?.from || "/");
    } catch (e) {
      toast.error(e.message || "فشل تسجيل Google");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFF] flex items-center justify-center px-4 py-10" dir="rtl">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-7">
          <div className="text-4xl font-black text-[#FF3D5A]">🛍️ نطاق للتسوق</div>
          <h1 className="text-2xl font-extrabold text-[#161629] mt-3">أهلًا بعودتك</h1>
          <p className="text-[#6B7280] mt-1">سجّل دخولك للاستمرار</p>
        </div>
        <div className="bg-white rounded-3xl border border-[#E8EAF2] p-6 brand-shadow-sm">
          <label className="block text-sm font-bold text-[#161629] mb-1.5 mt-2">الهاتف أو البريد الإلكتروني</label>
          <input
            data-testid="login-email-input"
            className="w-full h-13 bg-[#F0F2FA] rounded-2xl px-4 py-3.5 text-[#161629] outline-none focus:ring-2 focus:ring-[#FF3D5A]"
            placeholder="091XXXXXXX أو name@example.com"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
          <label className="block text-sm font-bold text-[#161629] mb-1.5 mt-3">كلمة المرور</label>
          <input
            data-testid="login-password-input"
            type="password"
            className="w-full bg-[#F0F2FA] rounded-2xl px-4 py-3.5 text-[#161629] outline-none focus:ring-2 focus:ring-[#FF3D5A]"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onLogin()}
          />
          <button
            data-testid="login-submit-button"
            disabled={busy}
            onClick={onLogin}
            className="w-full bg-[#FF3D5A] text-white font-extrabold text-lg py-4 rounded-2xl mt-6 hover:bg-[#FF2548] transition-colors disabled:opacity-60"
          >
            {busy ? "..." : "تسجيل الدخول"}
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#E8EAF2]" />
            <span className="text-[#6B7280] text-sm">أو</span>
            <div className="flex-1 h-px bg-[#E8EAF2]" />
          </div>

          <div className="flex items-center justify-center [&>div]:!w-full" data-testid="login-google-button">
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={() => toast.error("فشل تسجيل الدخول عبر Google")}
              locale="ar"
              text="continue_with"
              shape="pill"
              width="360"
            />
          </div>

          <Link to="/register" data-testid="go-to-register" className="block text-center text-[#6B7280] text-sm mt-5">
            ليس لديك حساب؟ <span className="text-[#FF3D5A] font-extrabold">إنشاء حساب</span>
          </Link>

          <button
            data-testid="skip-login-button"
            onClick={async () => { await setGuest(true); navigate("/"); }}
            className="w-full flex items-center justify-center gap-2 mt-4 py-3.5 rounded-2xl bg-[#F0F2FA] text-[#161629] font-bold hover:bg-[#E8EAF2] transition-colors"
          >
            <ArrowRightCircle size={20} className="text-[#FF3D5A]" /> تصفّح كزائر بدون تسجيل
          </button>
        </div>
      </div>
    </div>
  );
}
